import React, {useEffect, useState} from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Collapse,
  Divider,
  Step,
  StepLabel,
  Stepper,
  Typography
} from '@mui/material';
import {ArrowBack, Article, AutoAwesome, ExpandMore, Flight, FlightTakeoff, InfoOutlined, WarningAmber} from '@mui/icons-material';
import {t} from 'ttag';
import {DateTime} from 'luxon';
import {useLocation} from 'react-router-dom';
import {IFlight, IPassenger} from '../../store/apis/interfaces/flight';
import {
  IrregularityReason,
  IRREGULARITY_REASON_LIST,
  findCanonicalReason,
  buildCancellationText,
} from './irregularityReasonConfig';
import useFadeNavigate from './useFadeNavigate';
import FlightStatusBadge from './FlightStatusBadge';

const fmt = (iso: string | null) =>
  iso ? DateTime.fromISO(iso).toFormat('HH:mm') : '—';

const ataStaMinutes = (flight: IFlight): number => {
  const sta = flight.arrival?.scheduledTime;
  const ata = flight.arrival?.actualTime;
  if (!sta || !ata) return 0;
  return Math.max(0, Math.round(DateTime.fromISO(ata).diff(DateTime.fromISO(sta), 'minutes').minutes));
};

/** Re-exported for use in CancellationReviewPage */
export type CancellationReason = IrregularityReason;

const STEPS = [t`Declaration Type`, t`Cancellation Reason`, t`Review`];

interface LocationState {
  flight: IFlight;
  carrier: string;
  flightNumber: string;
  date: string;
  selectedPassengers: IPassenger[];
  declarationType: 'cancellation';
}

const setsEqual = (a: Set<string>, b: Set<string>) =>
  a.size === b.size && [...a].every((v) => b.has(v));

/**
 * Extract canonical reason codes from a cancelled flight.
 * Checks departure/arrival delays AND parses the status string
 * (e.g. "Cancelled due TC" → resolves TC → MTECH).
 */
const autoCodesFromFlight = (flight: IFlight): Set<string> => {
  const result = new Set<string>();

  // 1. From delay entries (may be populated even for cancellations)
  const allDelays = [
    ...(flight.departure.delays ?? []),
    ...(flight.arrival.delays ?? [])
  ].filter((d, i, arr) => arr.findIndex((x) => x.code === d.code) === i);

  for (const d of allDelays) {
    const canonical = findCanonicalReason(d.code);
    if (canonical) result.add(canonical.code);
  }

  // 2. From status field — e.g. "Cancelled due TC" → extract "TC"
  // 3. From status history — the current status may just be "Cancelled" with
  //    no reason, but an earlier status update for the same flight (before it
  //    settled) may have carried the reason, e.g. "Delayed due TC".
  const statusValues = [
    flight.status,
    ...Object.values(flight.flightChanges?.['Status'] ?? {})
  ].filter((s): s is string => Boolean(s));

  for (const status of statusValues) {
    const match = status.match(/due\s+(\w+)/i);
    if (match) {
      const canonical = findCanonicalReason(match[1]);
      if (canonical) result.add(canonical.code);
    }
  }

  return result;
};

const CancellationDetailsPage = () => {
  const navigate = useFadeNavigate();
  const location = useLocation();
  const state = location.state as LocationState | null;
  const storageKey = state ? `reasons-${state.carrier}-${state.flightNumber}-${state.date}-cancellation` : null;

  const autoCodes = state?.flight ? autoCodesFromFlight(state.flight) : new Set<string>();

  const [selectedCodes, setSelectedCodes] = useState<Set<string>>(() => {
    if (storageKey) {
      try {
        const raw = sessionStorage.getItem(storageKey);
        if (raw) return new Set<string>(JSON.parse(raw));
      } catch {}
    }
    return new Set(autoCodes);
  });
  const [hasError, setHasError] = useState(false);
  const [showAllReasons, setShowAllReasons] = useState(() => {
    if (storageKey) {
      try {
        const raw = sessionStorage.getItem(storageKey);
        if (raw) return (JSON.parse(raw) as string[]).some(code => !autoCodes.has(code));
      } catch {}
    }
    return false;
  });

  useEffect(() => {
    if (!storageKey) return;
    sessionStorage.setItem(storageKey, JSON.stringify([...selectedCodes]));
  }, [selectedCodes]);

  if (!state?.flight) {
    return (
      <Box sx={{textAlign: 'center', mt: 4}}>
        <Typography color='error' gutterBottom>
          {t`No flight data. Please go back and search again.`}
        </Typography>
        <Button
          variant='text'
          startIcon={<ArrowBack />}
          onClick={() => navigate('/declarations/flight')}
          sx={{mt: 1, color: 'text.secondary'}}
        >
          {t`Back to Search`}
        </Button>
      </Box>
    );
  }

  const {flight, carrier, flightNumber, date, selectedPassengers} = state;
  const formattedDate = DateTime.fromFormat(date, 'yyyy-MM-dd').toLocaleString(DateTime.DATE_FULL);
  const hasDelay = ataStaMinutes(flight) > 0;

  const passengerNames = selectedPassengers
    .map((p) => `${p.paxGivenName?.[0] ?? '?'}.${p.paxSurname || p.paxGivenName}`)
    .join('; ');

  // Raw delay entries from the system (for display in "Registered in System")
  const systemDelays = [
    ...(flight.departure.delays ?? []),
    ...(flight.arrival.delays ?? [])
  ].filter((d, i, arr) => arr.findIndex((x) => x.code === d.code) === i);

  // Status-parsed code for display when delays array is empty
  const statusMatch = flight.status?.match(/due\s+(\w+)/i)?.[1] ?? null;
  const statusCanonical = statusMatch ? findCanonicalReason(statusMatch) : null;

  const isManuallyAltered = !setsEqual(selectedCodes, autoCodes);

  const toggle = (code: string) => {
    const next = new Set(selectedCodes);
    if (next.has(code)) next.delete(code);
    else next.add(code);
    setSelectedCodes(next);
    if (next.size > 0) setHasError(false);
  };

  const autoReasonsList = IRREGULARITY_REASON_LIST.filter(r => autoCodes.has(r.code));
  const otherReasonsList = IRREGULARITY_REASON_LIST.filter(r => !autoCodes.has(r.code));

  const selectedReasons = IRREGULARITY_REASON_LIST.filter((r) => selectedCodes.has(r.code));
  const uniquePreviewReasons = selectedReasons.filter(
    (r, i, arr) => arr.findIndex((x) => x.textPT === r.textPT && x.textEN === r.textEN) === i
  );

  const handleContinue = () => {
    if (selectedCodes.size === 0) {
      setHasError(true);
      return;
    }
    navigate('/declarations/flight/declaration/cancellation/review', {
      state: {...state, selectedCancellationReasons: selectedReasons}
    });
  };

  return (
    <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
      {/* Flight header */}
      <Card elevation={2} sx={{borderTop: '3px solid', borderColor: 'error.main'}}>
        <CardContent sx={{py: 2, '&:last-child': {pb: 2}}}>
          <Box sx={{display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap'}}>
            <Box sx={{
              background: 'linear-gradient(135deg, #0288d1 0%, #01579b 100%)',
              color: 'white',
              borderRadius: 2,
              width: 48,
              height: 48,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              boxShadow: '0 3px 10px rgba(1,87,155,0.45)',
              gap: 0.25,
            }}>
              <Typography sx={{fontWeight: 900, fontSize: '0.9rem', letterSpacing: '0.1em', lineHeight: 1}}>
                {carrier.toUpperCase()}
              </Typography>
              <FlightTakeoff sx={{fontSize: '0.65rem', opacity: 0.75}} />
            </Box>
            <Box sx={{flex: 1, minWidth: 0}}>
              <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap'}}>
                <Typography variant='h6' fontWeight='bold' lineHeight={1.2}>
                  {carrier.toUpperCase()}{flightNumber}
                </Typography>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                  <Typography variant='body1' fontWeight='bold'>{flight.origin}</Typography>
                  <Flight sx={{fontSize: '1rem', color: 'text.secondary'}} />
                  <Typography variant='body1' fontWeight='bold'>{flight.destination}</Typography>
                </Box>
                <FlightStatusBadge status={flight.status} hasDelay={hasDelay} />
              </Box>
              <Typography variant='body2' color='text.secondary'>
                {formattedDate}
                {flight.departure.scheduledTime && ` · STD: ${fmt(flight.departure.scheduledTime)}`}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Passengers summary */}
      <Card variant='outlined' sx={{bgcolor: 'grey.50'}}>
        <CardContent sx={{py: 1.5, '&:last-child': {pb: 1.5}}}>
          <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
            <Article sx={{fontSize: '1.1rem', color: 'text.secondary'}} />
            <Typography variant='body2'>
              {t`Issue declaration for`}{' '}
              <Box component='span' fontWeight='bold'>{selectedPassengers.length}</Box>{' '}
              {t`passengers`}{': '}
              <Box component='span' fontWeight='bold'>{passengerNames}</Box>
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Stepper */}
      <Stepper activeStep={1} alternativeLabel>
        {STEPS.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Cancellation reason card */}
      <Card elevation={3}>
        <CardContent sx={{p: 3}}>
          <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 0.5}}>
            <InfoOutlined sx={{color: 'text.secondary'}} />
            <Typography variant='h6' fontWeight='bold'>{t`Cancellation Reason`}</Typography>
          </Box>
          <Typography variant='body2' color='text.secondary' sx={{mb: 3}}>
            {t`Select or confirm the reason(s) for the cancellation. They will be automatically translated into the appropriate language for the customer.`}
          </Typography>

          {/* System-registered reason */}
          <Box
            sx={{
              bgcolor: 'grey.50',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              px: 2,
              py: 1.5,
              mb: 3
            }}
          >
            <Typography
              variant='caption'
              color='text.secondary'
              sx={{fontWeight: 700, letterSpacing: '0.08em', display: 'block', mb: 0.75}}
            >
              {t`REASON REGISTERED IN SYSTEM`}
            </Typography>

            {systemDelays.length > 0 ? (
              systemDelays.map((d) => (
                <Box key={d.code} sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1}}>
                  <Typography variant='body2' sx={{lineHeight: 1.8}}>
                    <Box component='span' sx={{color: 'primary.main', fontWeight: 700}}>{d.code}</Box>
                    {' – '}{d.description}
                  </Typography>
                </Box>
              ))
            ) : statusCanonical ? (
              // Fallback: show code extracted from status string
              <Typography variant='body2' sx={{lineHeight: 1.8}}>
                <Box component='span' sx={{color: 'primary.main', fontWeight: 700}}>{statusMatch}</Box>
                {' – '}{statusCanonical.labelEN}
              </Typography>
            ) : statusMatch ? (
              // Code was extracted from the status string but is not in our mapping table
              <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                <WarningAmber sx={{fontSize: '1.1rem', color: 'warning.main', flexShrink: 0}} />
                <Typography variant='body2' sx={{lineHeight: 1.8}}>
                  <Box component='span' sx={{color: 'warning.dark', fontWeight: 700}}>{statusMatch}</Box>
                  {' – '}
                  <Box component='span' sx={{color: 'warning.dark'}}>
                    {t`Unrecognized code — defaulting to operational reasons. Please confirm the reason manually.`}
                  </Box>
                </Typography>
              </Box>
            ) : (
              <Typography variant='body2' color='text.secondary'>—</Typography>
            )}
          </Box>

          {/* Reason label row */}
          <Box sx={{mb: isManuallyAltered ? 1 : 1.5}}>
            <Typography variant='body2' fontWeight={600}>
              {t`Irregularity Reason`} *
              {hasError && (
                <Box component='span' sx={{color: 'error.main', ml: 1, fontWeight: 400, fontSize: '0.75rem'}}>
                  {t`Select at least one reason`}
                </Box>
              )}
            </Typography>
          </Box>

          {autoCodes.size > 0 && isManuallyAltered && (
            <Alert severity='warning' sx={{mb: 1.5, py: 0.5, fontSize: '0.8rem'}}>
              {t`You have changed the reason(s) suggested by the system. The system suggestion is shown with the`}{' '}
              <Box component='span' sx={{display: 'inline-flex', alignItems: 'center', gap: 0.4, px: 0.9, py: 0.2, borderRadius: '6px',
                background: 'linear-gradient(135deg, #29b6f6 0%, #0277bd 100%)', color: 'white', verticalAlign: 'middle', mx: 0.3,
                fontSize: '0.6rem', fontWeight: 800, letterSpacing: '0.08em', lineHeight: 1}}>
                <AutoAwesome sx={{fontSize: '0.65rem'}} />
                {t`AUTO`}
              </Box>
              {' '}{t`badge. Please confirm this change is intentional before continuing.`}
            </Alert>
          )}

          {/* Multi-select reason list */}
          <Box sx={{display: 'flex', flexDirection: 'column', gap: 1, mb: selectedReasons.length ? 3 : 0}}>

            {/* Auto-detected reasons — always visible */}
            {(autoCodes.size > 0 ? autoReasonsList : IRREGULARITY_REASON_LIST).map((reason) => {
              const isSelected = selectedCodes.has(reason.code);
              return (
                <Card
                  key={reason.code}
                  variant='outlined'
                  sx={{
                    cursor: 'pointer',
                    borderColor: isSelected ? 'error.main' : hasError ? 'error.light' : 'divider',
                    borderWidth: isSelected ? 2 : 1,
                    bgcolor: isSelected ? 'rgba(211,47,47,0.04)' : 'background.paper',
                    transition: 'border-color 0.15s, background-color 0.15s',
                    '&:hover': {borderColor: 'error.light'}
                  }}
                  onClick={() => toggle(reason.code)}
                >
                  <CardContent sx={{py: 1.25, px: 2, '&:last-child': {pb: 1.25}}}>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
                      <Checkbox checked={isSelected} onChange={() => toggle(reason.code)}
                        onClick={(e) => e.stopPropagation()} color='error' size='small' sx={{p: 0.25, flexShrink: 0}} />
                      <Chip label={reason.code} size='small'
                        variant={isSelected ? 'filled' : 'outlined'} color={isSelected ? 'error' : 'default'}
                        sx={{fontWeight: 700, fontFamily: 'monospace', fontSize: '0.72rem', height: 26, minWidth: 58, flexShrink: 0, letterSpacing: '0.03em'}} />
                      <Box sx={{flex: 1, minWidth: 0}}>
                        <Typography variant='body2' fontWeight={isSelected ? 600 : 400}>{reason.labelEN}</Typography>
                        <Typography variant='caption' color='text.secondary'>{reason.labelPT}</Typography>
                      </Box>
                      {autoCodes.size > 0 && (
                        <Box sx={{display: 'inline-flex', alignItems: 'center', gap: 0.4, px: 0.9, py: 0.35, borderRadius: '6px',
                          background: 'linear-gradient(135deg, #29b6f6 0%, #0277bd 100%)', color: 'white', flexShrink: 0, boxShadow: '0 1px 5px rgba(2,119,189,0.45)'}}>
                          <AutoAwesome sx={{fontSize: '0.65rem'}} />
                          <Typography sx={{fontSize: '0.6rem', fontWeight: 800, letterSpacing: '0.08em', lineHeight: 1}}>{t`AUTO`}</Typography>
                        </Box>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              );
            })}

            {/* Toggle + collapsed other reasons */}
            {autoCodes.size > 0 && otherReasonsList.length > 0 && (
              <>
                <Button
                  variant='text'
                  size='small'
                  endIcon={<ExpandMore sx={{transform: showAllReasons ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s'}} />}
                  onClick={() => setShowAllReasons(v => !v)}
                  sx={{alignSelf: 'flex-start', color: 'text.secondary', fontSize: '0.8rem', mt: 0.5}}
                >
                  {showAllReasons
                    ? t`Show fewer reasons`
                    : `${t`Show`} ${otherReasonsList.length} ${t`more reasons`}`}
                </Button>
                <Collapse in={showAllReasons}>
                  <Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
                    {otherReasonsList.map((reason) => {
                      const isSelected = selectedCodes.has(reason.code);
                      return (
                        <Card
                          key={reason.code}
                          variant='outlined'
                          sx={{
                            cursor: 'pointer',
                            borderColor: isSelected ? 'error.main' : hasError ? 'error.light' : 'divider',
                            borderWidth: isSelected ? 2 : 1,
                            bgcolor: isSelected ? 'rgba(211,47,47,0.04)' : 'background.paper',
                            transition: 'border-color 0.15s, background-color 0.15s',
                            '&:hover': {borderColor: 'error.light'}
                          }}
                          onClick={() => toggle(reason.code)}
                        >
                          <CardContent sx={{py: 1.25, px: 2, '&:last-child': {pb: 1.25}}}>
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
                              <Checkbox checked={isSelected} onChange={() => toggle(reason.code)}
                                onClick={(e) => e.stopPropagation()} color='error' size='small' sx={{p: 0.25, flexShrink: 0}} />
                              <Chip label={reason.code} size='small'
                                variant={isSelected ? 'filled' : 'outlined'} color={isSelected ? 'error' : 'default'}
                                sx={{fontWeight: 700, fontFamily: 'monospace', fontSize: '0.72rem', height: 26, minWidth: 58, flexShrink: 0, letterSpacing: '0.03em'}} />
                              <Box sx={{flex: 1, minWidth: 0}}>
                                <Typography variant='body2' fontWeight={isSelected ? 600 : 400}>{reason.labelEN}</Typography>
                                <Typography variant='caption' color='text.secondary'>{reason.labelPT}</Typography>
                              </Box>
                            </Box>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </Box>
                </Collapse>
              </>
            )}
          </Box>

          {/* Declaration text preview for selected reasons */}
          {selectedReasons.length > 0 && (
            <Box
              sx={{
                borderLeft: '3px solid',
                borderColor: 'success.main',
                bgcolor: 'grey.50',
                borderRadius: '0 8px 8px 0',
                px: 2,
                py: 1.5
              }}
            >
              <Typography
                variant='caption'
                color='text.secondary'
                sx={{fontWeight: 700, letterSpacing: '0.08em', display: 'block', mb: 1}}
              >
                {t`TEXT THAT WILL APPEAR IN THE DECLARATION`}
              </Typography>
              {uniquePreviewReasons.map((r, i) => {
                const sentence = buildCancellationText(r, `${carrier.toUpperCase()}${flightNumber}`);
                return (
                  <Box key={r.code} sx={{mb: i < uniquePreviewReasons.length - 1 ? 2 : 0}}>
                    <Box sx={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5}}>
                      <Box sx={{
                        bgcolor: 'rgba(46,125,50,0.06)',
                        border: '1px solid rgba(46,125,50,0.22)',
                        borderRadius: 1.5,
                        px: 1.5,
                        py: 1.25,
                      }}>
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75}}>
                          <Typography component='span' sx={{fontSize: '1rem', lineHeight: 1}}>🇵🇹</Typography>
                          <Typography variant='caption' sx={{fontWeight: 700, color: 'success.dark', letterSpacing: '0.04em'}}>
                            Português
                          </Typography>
                        </Box>
                        <Typography variant='body2' color='text.secondary'>
                          <em>"{sentence.pt}"</em>
                        </Typography>
                      </Box>
                      <Box sx={{
                        bgcolor: 'rgba(25,118,210,0.06)',
                        border: '1px solid rgba(25,118,210,0.22)',
                        borderRadius: 1.5,
                        px: 1.5,
                        py: 1.25,
                      }}>
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75}}>
                          <Typography component='span' sx={{fontSize: '1rem', lineHeight: 1}}>🇬🇧</Typography>
                          <Typography variant='caption' sx={{fontWeight: 700, color: 'primary.dark', letterSpacing: '0.04em'}}>
                            English
                          </Typography>
                        </Box>
                        <Typography variant='body2' color='text.secondary'>
                          <em>"{sentence.en}"</em>
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </CardContent>
      </Card>

      <Divider />

      {/* Footer */}
      <Box sx={{display: 'flex', justifyContent: 'space-between'}}>
        <Button variant='text' startIcon={<ArrowBack />} sx={{color: 'text.secondary'}} onClick={() => navigate(-1)}>
          {t`Back`}
        </Button>
        <Button variant='contained' onClick={handleContinue}>
          {t`Continue`}
        </Button>
      </Box>
    </Box>
  );
};

export default CancellationDetailsPage;
